import React, {useEffect, useRef, useState} from 'react';
import {NavLink} from "react-router-dom";
import styled from "styled-components";

// Assets
import IconLoaderCircle from "../../Assets/SVGs/Icons/Loading/LoaderCircle";
import IconAlertError from "../../Assets/SVGs/Icons/AlertAndInfo/AlertError";
import IconCheckMark from "../../Assets/SVGs/Icons/YesOrNo/CheckMark";

// Constants
import {RoutePaths} from "../../Constants/RoutePaths";

interface PopUpProps {
    isOpen: boolean;
    onClose: () => void;
    padding?: string | number;
    borderRadius?: string | number;
    isDarkMode?: boolean;
    progressMessage?: string;
    progressSteps: ProgressStep[];
    errorActionLabel?: string;
    onErrorAction?: () => void;
}

interface ProgressStep {
    percent: number;
    status: 'pending' | 'success' | 'error';
}

const ProgressPanel: React.FC<PopUpProps> = ({
    isOpen,
    onClose,
    progressMessage,
    isDarkMode,
    progressSteps,
    errorActionLabel,
    onErrorAction,
}) => {
    const
        popupRef = useRef<HTMLDivElement>(null),
        [transitionDirection, setTransitionDirection] = useState<string>("out"),
        hasError = progressSteps.some((step) => step.status === "error"),
        fullSuccess = progressSteps.some((step) => step.status === "success" && step.percent === 100),
        accountAlreadyExists = progressSteps.some(
            (step) => step.status === "error" && step.percent === 25
        ),

        handleErrorAction = () => {
            if (onErrorAction) {
                onErrorAction();
                return;
            }
            window.location.reload();
        },

        renderProgressSegments = () => {
            let completedPercent = 0;
            let errorOccurred = false;

            return progressSteps.map((step, index) => {
                if (errorOccurred) {
                    return null; // Don't render any steps after an error
                }

                let width = 0;
                if (step.status === 'success' || step.status === 'error') {
                    width = step.percent - completedPercent;
                    completedPercent = step.percent;
                }

                if (step.status === 'error') {
                    errorOccurred = true;
                }

                return (
                    <div
                        key={index}
                        className={`progress__segment progress__segment--${step.status}`}
                        style={{
                            width: `${width}%`,
                            opacity: step.status !== 'pending' ? 1 : 0.3
                        }}
                    />
                );
            }).filter(Boolean); // Remove null elements
        };

    useEffect(() => {
        let closeTimeout: NodeJS.Timeout;

        const handleOutsideClick = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                setTransitionDirection("out");
                closeTimeout = setTimeout(() => {
                    onClose();
                }, 450);
            }
        };

        if (isOpen) {
            setTransitionDirection("in");
            document.addEventListener('mousedown', handleOutsideClick);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            if (closeTimeout) {
                clearTimeout(closeTimeout);
            }
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen || !fullSuccess) return;

        const timeout = setTimeout(() => {
            // Use a full navigation so the newly-created auth/subscription state is refreshed.
            const fullUrl = `${window.location.origin}${RoutePaths.SignUpSuccess}`;
            window.location.href = fullUrl;
        }, 3500);

        return () => clearTimeout(timeout);
    }, [fullSuccess, isOpen]);

    if (!isOpen) return null;

    return (
        <div
            className={`popup popup--progress-panel ${isDarkMode ? 'is-dark-mode' : 'has-light-mode'} ${transitionDirection === "in" ? 'is-transitioning-in' : 'is-transitioning-out'}`}>
            <div className="popup__inner">
                {hasError ? IconAlertError() : (
                    fullSuccess ? IconCheckMark() : (<IconLoaderCircle className="popup__loader"/>)
                )}

                <h1>
                    {hasError ? "Error Occurred" : (
                        fullSuccess ? "Success!" : progressMessage
                    )}
                </h1>

                <div className="progress__bar-wrapper">
                    {renderProgressSegments()}
                </div>

                <p>
                    {hasError ? (
                        accountAlreadyExists ?
                            (
                                <>
                                    <span
                                        className="progress-panel__login-switch">Is this email already in use? <NavLink
                                        to={RoutePaths.Login}>Login</NavLink></span>
                                    <span onClick={handleErrorAction} style={{cursor: 'pointer', marginTop: '10px'}}>
                                        or {errorActionLabel ?? "close and refresh"}
                                    </span>
                                </>
                            )
                            :
                            (
                                <span onClick={handleErrorAction} style={{cursor: 'pointer'}}>
                                    {errorActionLabel ?? "Close and refresh"}
                                </span>
                            )
                    ) : (
                        fullSuccess && (<span><IconLoaderCircle className="popup__loader"/> Redirecting...</span>)
                    )}
                </p>
            </div>
        </div>
    );
};

export default ProgressPanel;
